import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
    fetchFilterOptions,
    fetchCustomers,
    fetchSettledBillsData,
    setFromDate,
    setToDate,
    setSelectedBranch,
    setSelectedCustomer,
    resetFilters,
    selectBranches,
    selectCustomers,
    selectCustomersLoading,
    selectSelectedBranch,
    selectSelectedCustomer,
    selectFromDate,
    selectToDate,
    selectLoading
} from '../../store/slices/creditSettlementReportsSlice';
import './billreports.css';

const CreditSettlementReportsHeader = () => {
    const dispatch = useDispatch();
    const managerBranchInitialized = useRef(false);

    const role = (localStorage.getItem('role') || '').toUpperCase();
    const isManager = role === 'MANAGER';
    const storedBranchId = localStorage.getItem('branchid') || '';
    const storedBranchName = localStorage.getItem('branch') || storedBranchId;

    // Redux selectors
    const branches = useSelector(selectBranches);
    const customers = useSelector(selectCustomers);
    const customersLoading = useSelector(selectCustomersLoading);
    const selectedBranch = useSelector(selectSelectedBranch);
    const selectedCustomer = useSelector(selectSelectedCustomer);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const loading = useSelector(selectLoading);

    const customerOptions = customers.map((name) => ({ label: name, value: name }));

    // Initialize component - fetch filter options
    useEffect(() => {
        const storedClientid = localStorage.getItem('clientid');
        if (storedClientid) {
            dispatch(fetchFilterOptions({ rolename: role || 'CEO', clientname: storedClientid }));
        }
    }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // MANAGER: branch is hidden, always the manager's own branch
    useEffect(() => {
        if (!isManager || managerBranchInitialized.current || !storedBranchId) return;
        dispatch(setSelectedBranch({ branch_id: storedBranchId, branch_name: storedBranchName }));
        managerBranchInitialized.current = true;
    }, [isManager, storedBranchId, storedBranchName, dispatch]);

    // Load customers whenever a branch is chosen
    useEffect(() => {
        if (!selectedBranch || !selectedBranch.branch_id) return;
        const clientId = localStorage.getItem('clientid');
        dispatch(fetchCustomers({ branchId: selectedBranch.branch_id, clientId }));
    }, [selectedBranch, dispatch]);

    // Apply filters - fetch settled bill data
    const handleApplyFilters = () => {
        if (!fromDate || !toDate) {
            return;
        }

        dispatch(
            fetchSettledBillsData({
                fromDate,
                toDate,
                branchId: selectedBranch?.branch_id || '',
                custName: selectedCustomer || ''
            })
        );
    };

    // Reset filters
    const handleResetFilters = () => {
        dispatch(resetFilters());
        if (isManager && storedBranchId) {
            dispatch(setSelectedBranch({ branch_id: storedBranchId, branch_name: storedBranchName }));
        }
    };

    return (
        <>
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Credit Settlement Report
                </h2>
                <p className="text-color-secondary">View credit bills that have been settled, with settlement date and remarks</p>
            </div>

            {/* Filter Section */}
            <Card className="mb-3 bill-reports-filter-card">
                <div className="flex flex-wrap align-items-end bill-reports-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                        <label htmlFor="fromDate">From Date <span className="text-red-500">*</span></label>
                        <Calendar id="fromDate" value={fromDate} onChange={(e) => dispatch(setFromDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                        <label htmlFor="toDate">To Date <span className="text-red-500">*</span></label>
                        <Calendar id="toDate" value={toDate} onChange={(e) => dispatch(setToDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                    </div>

                    {!isManager && (
                        <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown id="branch" value={selectedBranch} options={branches} onChange={(e) => dispatch(setSelectedBranch(e.value))} optionLabel="branch_name" placeholder="All Branches" className="w-full" filter showClear />
                        </div>
                    )}

                    <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                        <label htmlFor="customer">Customer</label>
                        <Dropdown
                            id="customer"
                            value={selectedCustomer}
                            options={customerOptions}
                            onChange={(e) => dispatch(setSelectedCustomer(e.value))}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={selectedBranch?.branch_id ? 'All Customers' : 'Select a branch first'}
                            className="w-full"
                            filter
                            showClear
                            disabled={!selectedBranch?.branch_id}
                            loading={customersLoading}
                        />
                    </div>

                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={handleApplyFilters} disabled={!fromDate || !toDate || loading} loading={loading} />
                        <Button icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} tooltip="Reset" />
                    </div>
                </div>
            </Card>
        </>
    );
};

export default CreditSettlementReportsHeader;
